import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../config/api.config';

const InvestorNetworksList = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { tenantId } = useParams();
    const [searchParams] = useSearchParams();
    const tenantPath = tenantId || user?.tenantIdentifier || 'default';

    const [loading, setLoading] = useState(true);
    const [networks, setNetworks] = useState([]);
    const [startupTab, setStartupTab] = useState('through-network');
    const [individualStartups, setIndividualStartups] = useState([]);
    const [networkLinkedStartups, setNetworkLinkedStartups] = useState([]);
    const [startupSearchQuery, setStartupSearchQuery] = useState('');
    const [startupSearchLoading, setStartupSearchLoading] = useState(false);
    const [startupSearchResult, setStartupSearchResult] = useState(null);
    const [linkModal, setLinkModal] = useState(null);
    const [linkForm, setLinkForm] = useState({
        investmentAmount: '',
        investmentStage: 'SEED',
        investmentType: 'EQUITY',
        currency: 'INR',
        investmentDate: new Date().toISOString().split('T')[0],
        notes: ''
    });
    const [linkSubmitting, setLinkSubmitting] = useState(false);
    const [toast, setToast] = useState(null);

    const activeSection = useMemo(() => {
        const section = searchParams.get('section');
        return section === 'startups' ? 'startups' : 'networks';
    }, [searchParams]);

    const authHeaders = useMemo(
        () => ({
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
        }),
        []
    );

    const loadNetworksData = useCallback(async () => {
        if (user?.role !== 'INVESTOR' || !user?.id) return;
        setLoading(true);
        try {
            const res = await fetch(getApiUrl('users/my-linked-networks'), {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            let data = [];
            if (res.ok) {
                const parsed = await res.json();
                data = Array.isArray(parsed) ? parsed : [];
            }

            if (!data.length) {
                const meRes = await fetch(getApiUrl('users/me'), {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                if (meRes.ok) {
                    const me = await meRes.json();
                    if (me?.investorNetworkId) {
                        const singleRes = await fetch(getApiUrl(`users/my-linked-network/${me.investorNetworkId}`), {
                            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                        });
                        if (singleRes.ok) {
                            const singleData = await singleRes.json();
                            if (singleData?.network) {
                                data = [{
                                    id: singleData.network.id,
                                    name: singleData.network.name,
                                    email: singleData.network.email,
                                    phoneNumber: singleData.network.phoneNumber,
                                    organizationName: singleData.network.organizationName,
                                    organizationType: singleData.network.organizationType,
                                    investedStartupCount: Array.isArray(singleData.investedStartups) ? singleData.investedStartups.length : 0
                                }];
                            }
                        }
                    }
                }
            }

            setNetworks(data);

            const portfolioRes = await fetch(getApiUrl(`investments/investor/${user.id}/portfolio`), {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            if (portfolioRes.ok) {
                const portfolioData = await portfolioRes.json();
                setIndividualStartups(Array.isArray(portfolioData) ? portfolioData : []);
            } else {
                setIndividualStartups([]);
            }

            if (data.length) {
                const detailsResults = await Promise.all(
                    data.map(async (network) => {
                        try {
                            const r = await fetch(getApiUrl(`users/my-linked-network/${network.id}`), {
                                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                            });
                            if (!r.ok) return [];
                            const detail = await r.json();
                            const startups = Array.isArray(detail?.investedStartups) ? detail.investedStartups : [];
                            return startups.map((s) => ({
                                ...s,
                                networkId: network.id,
                                networkName: network.organizationName || network.name || 'Network'
                            }));
                        } catch (_) {
                            return [];
                        }
                    })
                );
                setNetworkLinkedStartups(detailsResults.flat());
            } else {
                setNetworkLinkedStartups([]);
            }
        } catch (e) {
            console.error('Failed to load linked networks:', e);
            setNetworks([]);
            setIndividualStartups([]);
            setNetworkLinkedStartups([]);
        } finally {
            setLoading(false);
        }
    }, [user?.role, user?.id]);

    useEffect(() => {
        if (user?.role !== 'INVESTOR') {
            navigate(`/${tenantPath}/investor/dashboard`, { replace: true });
            return;
        }
        loadNetworksData();
    }, [user?.role, tenantPath, navigate, loadNetworksData]);

    const searchStartupsForPortfolio = async () => {
        const q = startupSearchQuery.trim();
        if (!q) {
            setToast({ type: 'info', message: 'Enter an email or startup name to search.' });
            return;
        }
        const params = new URLSearchParams();
        if (q.includes('@')) {
            params.set('email', q);
        } else {
            params.set('name', q);
        }
        setStartupSearchLoading(true);
        setStartupSearchResult(null);
        try {
            const res = await fetch(getApiUrl(`users/my-network/search-startup?${params}`), {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setToast({ type: 'error', message: data.error || 'Search failed.' });
                return;
            }
            setStartupSearchResult(data);
        } catch (e) {
            console.error(e);
            setToast({ type: 'error', message: 'Search failed. Try again.' });
        } finally {
            setStartupSearchLoading(false);
        }
    };

    const openLinkPortfolioModal = (startup) => {
        setLinkModal(startup);
        setLinkForm((f) => ({
            ...f,
            investmentAmount: '',
            investmentDate: new Date().toISOString().split('T')[0]
        }));
    };

    const submitLinkToPortfolio = async () => {
        if (!linkModal?.id) return;
        const amount = Number.parseFloat(String(linkForm.investmentAmount).replace(/,/g, ''));
        if (Number.isNaN(amount) || amount <= 0) {
            setToast({ type: 'info', message: 'Enter a valid investment amount greater than zero.' });
            return;
        }
        setLinkSubmitting(true);
        try {
            const res = await fetch(getApiUrl('investments/self'), {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    startupId: linkModal.id,
                    investmentAmount: amount,
                    investmentStage: linkForm.investmentStage,
                    investmentType: linkForm.investmentType,
                    currency: linkForm.currency,
                    investmentDate: linkForm.investmentDate || new Date().toISOString().split('T')[0],
                    notes: linkForm.notes?.trim() || undefined
                })
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setLinkModal(null);
                setToast({ type: 'success', message: 'Startup added to your portfolio.' });
                await loadNetworksData();
                setStartupSearchResult(null);
                setStartupSearchQuery('');
                return;
            }
            if (res.status === 402 || data.code === 'INVESTOR_ACTIVITY_REQUIRED') {
                setToast({
                    type: 'error',
                    message: data.error || data.message || 'Pay the one-time investor activity fee to link startups.'
                });
                return;
            }
            setToast({ type: 'error', message: data.message || data.error || 'Could not add startup. It may already be in your portfolio.' });
        } catch (e) {
            console.error(e);
            setToast({ type: 'error', message: 'Request failed. Please try again.' });
        } finally {
            setLinkSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
            <main className="min-h-screen overflow-auto">
                <div className="max-w-7xl mx-auto px-6 py-6">
                    {toast && (
                        <div
                            className={`mb-4 rounded-xl border px-4 py-3 text-sm ${
                                toast.type === 'success'
                                    ? 'bg-green-50 border-green-200 text-green-800'
                                    : toast.type === 'error'
                                        ? 'bg-red-50 border-red-200 text-red-800'
                                        : 'bg-amber-50 border-amber-200 text-amber-900'
                            }`}
                        >
                            <div className="flex justify-between gap-4 items-start">
                                <p>{toast.message}</p>
                                <button type="button" className="text-gray-500 hover:text-gray-800 shrink-0" onClick={() => setToast(null)} aria-label="Dismiss">
                                    ×
                                </button>
                            </div>
                        </div>
                    )}
                    {activeSection === 'networks' ? (
                        <>
                            <div className="mb-6">
                                <h1 className="text-2xl font-bold text-gray-900">My Networks</h1>
                                <p className="text-sm text-gray-500 mt-1">Networks linked to your investments</p>
                            </div>

                            {loading ? (
                                <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-600" /></div>
                            ) : networks.length === 0 ? (
                                <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                                    <p className="text-gray-500">No linked networks found.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {networks.map((network) => (
                                        <div key={network.id} className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                                            <div className="flex items-center space-x-4 mb-3">
                                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                                                    <span className="text-white font-semibold">{(network.organizationName || network.name || 'N').charAt(0).toUpperCase()}</span>
                                                </div>
                                                <div className="min-w-0">
                                                    <h3 className="font-semibold text-gray-900 truncate">{network.organizationName || network.name}</h3>
                                                    <p className="text-sm text-gray-500 truncate">{network.email || '—'}</p>
                                                </div>
                                            </div>
                                            <p className="text-sm text-gray-600 mb-4">{network.investedStartupCount || 0} invested startup(s)</p>
                                            <button
                                                type="button"
                                                onClick={() => navigate(`/${tenantPath}/investor/networks/${network.id}?tab=investors`)}
                                                className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition"
                                            >
                                                View Details
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="mb-6">
                                <h1 className="text-2xl font-bold text-gray-900">My Startups</h1>
                                <p className="text-sm text-gray-500 mt-1">Startups you invested in, grouped by how they are linked</p>
                            </div>

                            <div className="bg-white rounded-xl border border-gray-200 mb-4">
                                <div className="px-4 py-3 border-b border-gray-100">
                                    <nav className="flex -mb-px space-x-8">
                                        <button
                                            type="button"
                                            onClick={() => setStartupTab('through-network')}
                                            className={`${startupTab === 'through-network' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm`}
                                        >
                                            Through Network
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setStartupTab('individual')}
                                            className={`${startupTab === 'individual' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm`}
                                        >
                                            Individually Invested
                                        </button>
                                    </nav>
                                </div>
                                <div className="p-4">
                                    {loading ? (
                                        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-600" /></div>
                                    ) : startupTab === 'through-network' ? (
                                        networkLinkedStartups.length === 0 ? (
                                            <div className="text-center py-8 space-y-2">
                                                <p className="text-gray-500">No startups found through linked networks.</p>
                                                <p className="text-sm text-indigo-600">
                                                    To add a startup you invested in directly, open the <button type="button" className="font-semibold underline" onClick={() => setStartupTab('individual')}>Individually Invested</button> tab.
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                {networkLinkedStartups.map((s, idx) => (
                                                    <div key={`${s.networkId}-${s.id}-${idx}`} className="border border-gray-200 rounded-lg p-3 flex items-center justify-between">
                                                        <div className="min-w-0">
                                                            <p className="font-medium text-gray-900 truncate">{s.organizationName || s.name || 'Startup'}</p>
                                                            <p className="text-sm text-gray-500 truncate">{s.email || '—'}</p>
                                                            <p className="text-xs text-indigo-600 mt-1">Network: {s.networkName}</p>
                                                        </div>
                                                        {s.id && (
                                                            <button
                                                                type="button"
                                                                onClick={() => navigate(`/${tenantPath}/investor/startups/${s.id}?tab=overview`)}
                                                                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                                            >
                                                                View
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )
                                    ) : (
                                        <div className="space-y-6">
                                            <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-4">
                                                <h3 className="text-sm font-semibold text-gray-900">Add a startup to your portfolio</h3>
                                                <p className="text-sm text-gray-600 mt-1 mb-3">
                                                    Search by the startup&apos;s account email or company name. They must already be registered on the platform. You will record your investment amount to link them to your portfolio.
                                                </p>
                                                <div className="flex flex-wrap items-center gap-3">
                                                    <input
                                                        type="text"
                                                        value={startupSearchQuery}
                                                        onChange={(e) => {
                                                            setStartupSearchResult(null);
                                                            setStartupSearchQuery(e.target.value);
                                                        }}
                                                        onKeyDown={(e) => e.key === 'Enter' && searchStartupsForPortfolio()}
                                                        placeholder="Email or startup name"
                                                        className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={searchStartupsForPortfolio}
                                                        disabled={startupSearchLoading}
                                                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium disabled:opacity-50"
                                                    >
                                                        {startupSearchLoading ? 'Searching…' : 'Search'}
                                                    </button>
                                                </div>
                                                {startupSearchResult && (
                                                    <div className="mt-4 p-3 rounded-lg border border-gray-200 bg-white">
                                                        {!startupSearchResult.found ? (
                                                            <p className="text-sm text-gray-600">
                                                                No startup found. Ask the founder to sign up first, then search again.
                                                            </p>
                                                        ) : (
                                                            <ul className="space-y-2">
                                                                {(startupSearchResult.startups || []).map((s) => (
                                                                    <li
                                                                        key={s.id}
                                                                        className="flex flex-wrap items-center justify-between gap-3 py-2 border-b border-gray-100 last:border-0"
                                                                    >
                                                                        <div>
                                                                            <p className="font-medium text-gray-900">{s.organizationName || s.name}</p>
                                                                            <p className="text-sm text-gray-500">{s.email}</p>
                                                                        </div>
                                                                        {s.alreadyLinked ? (
                                                                            <span className="text-sm text-green-700 font-medium">In your portfolio</span>
                                                                        ) : (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => openLinkPortfolioModal(s)}
                                                                                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                                                            >
                                                                                Link to portfolio
                                                                            </button>
                                                                        )}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {individualStartups.length === 0 ? (
                                                <p className="text-gray-500 text-center py-6">No individually invested startups yet. Use the search above to add one.</p>
                                            ) : (
                                                <div className="space-y-3">
                                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Your portfolio</p>
                                                    {individualStartups.map((s, idx) => (
                                                        <div key={`${s.id}-${idx}`} className="border border-gray-200 rounded-lg p-3 flex items-center justify-between">
                                                            <div className="min-w-0">
                                                                <p className="font-medium text-gray-900 truncate">{s.organizationName || s.name || 'Startup'}</p>
                                                                <p className="text-sm text-gray-500 truncate">{s.email || '—'}</p>
                                                            </div>
                                                            {s.id && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => navigate(`/${tenantPath}/investor/startups/${s.id}?tab=overview`)}
                                                                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                                                >
                                                                    View
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </main>

            {linkModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                    <div className="bg-white rounded-2xl max-w-md w-full shadow-xl border border-gray-200">
                        <div className="p-5 border-b border-gray-100 flex justify-between items-start gap-3">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">Record investment</h3>
                                <p className="text-sm text-gray-500 mt-0.5">
                                    {linkModal.organizationName || linkModal.name}
                                </p>
                            </div>
                            <button
                                type="button"
                                className="text-gray-400 hover:text-gray-700 text-xl leading-none"
                                onClick={() => !linkSubmitting && setLinkModal(null)}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <div className="p-5 space-y-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Amount</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="any"
                                    value={linkForm.investmentAmount}
                                    onChange={(e) => setLinkForm((f) => ({ ...f, investmentAmount: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    placeholder="e.g. 500000"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Stage</label>
                                    <select
                                        value={linkForm.investmentStage}
                                        onChange={(e) => setLinkForm((f) => ({ ...f, investmentStage: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    >
                                        <option value="SEED">Seed</option>
                                        <option value="PRE_SEED">Pre-seed</option>
                                        <option value="SERIES_A">Series A</option>
                                        <option value="SERIES_B">Series B</option>
                                        <option value="OTHER">Other</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Currency</label>
                                    <select
                                        value={linkForm.currency}
                                        onChange={(e) => setLinkForm((f) => ({ ...f, currency: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    >
                                        <option value="INR">INR</option>
                                        <option value="USD">USD</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Investment date</label>
                                <input
                                    type="date"
                                    value={linkForm.investmentDate}
                                    onChange={(e) => setLinkForm((f) => ({ ...f, investmentDate: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                />
                            </div>
                            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg p-2">
                                A one-time activity fee may be required before you can be linked to startups. If you see an error, complete payment under your subscription or activity fee page.
                            </p>
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    className="flex-1 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                    onClick={() => setLinkModal(null)}
                                    disabled={linkSubmitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                    onClick={submitLinkToPortfolio}
                                    disabled={linkSubmitting}
                                >
                                    {linkSubmitting ? 'Saving…' : 'Save to portfolio'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InvestorNetworksList;
